import { FilingFrequency, TaxType } from '@/types/filings';
import { CountryCode } from '@/types/location';
import { RegistrationCategory } from '@/types/registrations';
import { z } from 'zod';

// Single registration object schema
export const registrationCreateSchema = z
  .object({
    country_code: z.enum(CountryCode, { message: 'Country is required' }),
    state_code: z.string().min(1, 'Jurisdiction is required'),
    tax_type: z.enum(TaxType),
    filing_frequency: z.enum(FilingFrequency).optional(),
    type: z.enum(RegistrationCategory).optional(),
    registration_date: z.string().optional(),
    registration_email: z.email().optional(),
    username: z.string().optional(),
    // Fiscal-year anchor (1-12): the month each recurring period ends on when offset from
    // the calendar. Null/omitted keeps the calendar grid. Only sent for supported
    // jurisdictions (HI quarterly/semiannual, CA and DC annual, BC quarterly); the backend rejects
    // it for any other jurisdiction/frequency.
    period_end_month: z.number().int().min(1).max(12).nullish(),
  })
  .superRefine((value, ctx) => {
    if (value.tax_type === TaxType.RETAIL_DELIVERY_FEE) {
      return;
    }
    if (!value.filing_frequency) {
      ctx.addIssue({ code: 'custom', path: ['filing_frequency'], message: 'Filing frequency is required' });
    }
    if (!value.type) {
      ctx.addIssue({ code: 'custom', path: ['type'], message: 'Category is required' });
    }
  });

// Bulk create array schema (used by the API)
export const registrationCreateApiSchema = z.object({
  registrations: z.array(registrationCreateSchema),
});

export type RegistrationCreate = z.infer<typeof registrationCreateSchema>;
export type RegistrationCreatePayload = z.infer<typeof registrationCreateApiSchema>;
